import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'
import { orderKeys } from '@/entities/order/model/order.queryKeys'
import { reconcileZebraOfflineScanWithOrdersCache } from './reconcileZebraOfflineScanWithOrdersCache'
import { replayZebraEventQueue } from './replayZebraEventQueue'
import { peekAllZebraEvents } from './zebraEventQueue'

const RECONNECT_DEBOUNCE_MS = 400

function isOrdersListQueryKey(key: readonly unknown[]): boolean {
  const target = orderKeys.orders()
  return key.length === target.length && key.every((segment, i) => segment === target[i])
}

/**
 * On reconnect: flushes the offline event queue to the backend, refetches orders,
 * then reconciles local Zebra scan state with the updated cache.
 * Also reconciles when the orders query cache updates (e.g. first successful fetch).
 *
 * BD-8547: replay also runs on mount when the queue is not empty. The queue outlives the
 * page, so events left by a previous session need replaying at startup — `online` only
 * fires on a transition, so a restart that comes up already connected would never raise it
 * and those events would wait for the next drop in connectivity.
 */
export function useZebraOfflineScanReconnectSync(): void {
  const queryClient = useQueryClient()
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  useEffect(() => {
    const runAfterRefetch = () => {
      void replayZebraEventQueue().finally(() => {
        void queryClient
          .refetchQueries({ queryKey: orderKeys.orders() })
          .finally(() => {
            reconcileZebraOfflineScanWithOrdersCache(queryClient)
          })
      })
    }

    const scheduleAfterRefetch = () => {
      if (debounceTimerRef.current !== undefined) {
        clearTimeout(debounceTimerRef.current)
      }
      debounceTimerRef.current = setTimeout(() => {
        debounceTimerRef.current = undefined
        runAfterRefetch()
      }, RECONNECT_DEBOUNCE_MS)
    }

    reconcileZebraOfflineScanWithOrdersCache(queryClient)

    if (peekAllZebraEvents().length > 0) {
      runAfterRefetch()
    }

    const unsubscribeCache = queryClient.getQueryCache().subscribe((event) => {
      if (!isOrdersListQueryKey(event.query.queryKey)) {
        return
      }
      if (event.type !== 'updated' && event.type !== 'added') {
        return
      }
      if (!event.query.state.data) {
        return
      }
      reconcileZebraOfflineScanWithOrdersCache(queryClient)
    })

    window.addEventListener('online', scheduleAfterRefetch)
    return () => {
      window.removeEventListener('online', scheduleAfterRefetch)
      unsubscribeCache()
      if (debounceTimerRef.current !== undefined) {
        clearTimeout(debounceTimerRef.current)
      }
    }
  }, [queryClient])
}
